/**
 * Manual Line Mode
 *
 * Detects lines in the scene, allows user interaction with them,
 * and creates shapes/rooms from line intersections.
 *
 * Features:
 * - Automatic line detection on camera movement
 * - Interactive line highlighting and selection
 * - Shape creation from line intersections
 * - Room conversion from completed shapes
 */

import { Viewer } from '../../../../viewer/viewer';
import { IPersona } from '../../../types';
import * as THREE from 'three';
import { LineDetectionService } from '../../modules/line-detection/LineDetectionService';
import { LineRenderer } from '../../modules/line-detection/LineRenderer';
import { ShapeBuilder } from '../../modules/line-detection/ShapeBuilder';
import { DetectedLine, LineShape, LineDetectionConfig, LinePrompt, PromptConnection } from '../../modules/line-detection/types';
import { applyConstraintSnap } from '../../modules/line-detection/ConstraintSnapHook';
import { ConstraintSnapVisuals } from '../../modules/line-detection/ConstraintSnapVisuals';
import { EdgeOpController } from '../../modules/line-detection/EdgeOpController';
import { CoordinateTransformer } from '../../modules/utils/CoordinateTransformer';
import { LineSelectionHitTester } from '../../modules/line-detection/LineSelectionHitTester';
import { LinePromptVisualizer } from '../../modules/line-detection/LinePromptVisualizer';
import { PromptSnapshot, LineToolContext } from './types';
import { PromptAligner } from '../../modules/line-detection/PromptAligner';
import { DensitySnapper } from '../../modules/line-detection/DensitySnapper';
import { BoxExtrusionHandler } from '../../modules/line-detection/BoxExtrusionHandler';
import { ShapeRenderer as LineShapeRenderer } from '../../modules/line-detection/ShapeRenderer';
import { PromptDragHandler, PromptSphereDragState } from '../../modules/line-detection/PromptDragHandler';
import { PromptDeletionManager } from '../../modules/line-detection/PromptDeletionManager';
import { LineDetectionDebugWidget } from '../../widgets/LineDetectionDebugWidget';
import { ClipTask } from '../../../../defines';
import { PointoramaPlane } from '../../../../utils/PointoramaPlane';
import type { EditableShape } from './EditableShape';
import { BasePlaneState } from './SelectionContextProvider';
import { AreaSelector } from '../../modules/selection/AreaSelector';
import { ToolSettingsChangeEvent } from '../../../ToolSettings';
import { ImageCapture } from '../../modules/capture/ImageCapture';
import { SparkleParticleSystem } from '../../modules/segmentation/SparkleParticleSystem';
import { PointCloudOctree } from '../../../../PointCloudOctree';

export class ManualLineMode {
  protected viewer: Viewer;
  protected persona: IPersona;
  private parentTool: LineToolContext;

  // Base-plane state (passed from parent LineTool)
  protected currentBasePlane: BasePlaneState | null = null;

  // Core modules
  private lineDetectionService: LineDetectionService;
  private lineRenderer: LineRenderer;
  private shapeBuilder: ShapeBuilder;
  private coordinateTransformer: CoordinateTransformer;
  private areaSelector: AreaSelector;
  private hitTester: LineSelectionHitTester;
  private visualizer: LinePromptVisualizer;

  // Extracted modules
  private promptAligner: PromptAligner;
  private densitySnapper: DensitySnapper;
  private boxExtrusionHandler: BoxExtrusionHandler;
  private shapeRendererModule: LineShapeRenderer;
  private promptDragHandler: PromptDragHandler;
  private deletionManager: PromptDeletionManager;

  // Shared infrastructure (aliases to LineTool-owned objects via LineToolContext)
  private planePreviewScene: THREE.Scene;
  private mouse: THREE.Vector2;

  // Debug widget
  private debugWidget: LineDetectionDebugWidget | null = null;

  // State
  private detectedLines: DetectedLine[] = [];
  private linePrompts: LinePrompt[] = []; // Persistent prompts with world position and direction
  private nextPromptId = 0;

  // Shape state (rendering delegated to shapeRendererModule)
  private isDetecting = false;
  private lastDetectionTime = 0;
  private detectionDebounceMs = 20; // Wait 500ms after camera stops moving

  // Two-click line creation state
  private firstClickPoint: THREE.Vector3 | null = null; // First point for manual line creation

  // Prompt hover state
  private hoveredPromptId: string | null = null; // ID of currently hovered prompt

  // Preview line hover state (for detected lines before clicking)
  private hoveredPreviewLineId: string | null = null; // ID of currently hovered preview line (sphere or line)

  // Shift key state for showing all preview lines
  private shiftDown = false;

  // Control key state
  private ctrlDown = false;
  // Perpendicular snap state (activated by Shift when prompts exist)
  private perpendicularSnapTarget: {
    prompt: LinePrompt;
    snapPoint: THREE.Vector3; // Point on the prompt line closest to cursor
    previewEndPoint: THREE.Vector3; // Where the preview line should end (at cursor)
    distance: number; // Distance from cursor to the prompt line
  } | null = null;
  // Locked prompt ID for shift-hover snap targeting
  private shiftLockedPromptId: string | null = null;

  // Drag box selection for aligning prompts
  private dragBoxStart: THREE.Vector2 | null = null;
  // Right-drag box for deleting prompts
  private rightDragStart: THREE.Vector2 | null = null;
  private rightDragWasBox = false; // True if right-drag opened a selection box
  private dragStartWorldPoint: THREE.Vector3 | null = null; // World position at drag start for line creation

  // Drag from prompt line to create a box (extrusion)
  private promptLineDrag: {
    prompt: LinePrompt; // The prompt being dragged from (may switch during drag when snappedToEndpoint)
    startPoint: THREE.Vector3; // Point on the prompt line where drag started
    side: 'positive' | 'negative'; // Which side of the prompt the drag started from
    snappedToEndpoint?: boolean; // True when drag started from an endpoint/intersection
    connectedPrompt?: LinePrompt; // The other prompt at the intersection (for edge switching)
  } | null = null;

  // Density-snapped box edges computed during prompt line drag preview
  private snappedBoxEdges: Array<[THREE.Vector3, THREE.Vector3]> | null = null;

  // Hint override tracking (avoids redundant push/pop on every mousemove)
  private hoverOverrideId: string | null = null;

  // Drag a prompt sphere to reposition it with pixel alignment
  private promptSphereDrag: PromptSphereDragState | null = null;

  // Last mouse position during prompt sphere drag (for re-running on shift toggle)
  private lastDragMouse: THREE.Vector2 | null = null;

  // Camera movement tracking
  private cameraMoving = false;
  private cameraMoveTimeout: number | null = null;
  private lastCameraPosition: THREE.Vector3 | null = null;
  private lastCameraTarget: THREE.Vector3 | null = null;

  // Cancellation support for line detection
  private detectionAbortController: AbortController | null = null;

  // Shape edit mode state. Polymorphic across kinds via EditableShape.
  private editingShape: EditableShape | null = null;
  private editingShapeOriginalPolygon: THREE.Vector3[] | null = null;

  // Settings for custom segment isolation
  private settings = {
    enableCustomSegmentIsolation: false,
    segmentIsolationTolerance: 0.1, // in meters
    segmentIsolationHeight: 1.2, // in meters
    temporaryPointSize: 1.0, // temporary point size override (persists across tool activations)
  };

  // Store original point sizes for restoration when tool exits
  private originalPointSizes: Map<PointCloudOctree, number> = new Map();

  // Track if point size has been initialized from point cloud (only do this once)
  private pointSizeInitialized = false;

  // Depth segmentation (position map + hover preview)
  private imageCapture: ImageCapture;
  private sparkleParticles: SparkleParticleSystem;
  private constraintSnapVisuals: ConstraintSnapVisuals;
  private edgeOpController: EdgeOpController;

  constructor(viewer: Viewer, persona: IPersona, config: LineDetectionConfig | undefined, parentTool: LineToolContext) {
    this.viewer = viewer;
    this.persona = persona;
    this.parentTool = parentTool;

    // Aliases to shared infrastructure from LineTool (LineToolContext)
    this.coordinateTransformer = parentTool.coordinateTransformer;
    this.planePreviewScene = parentTool.planePreviewScene;
    this.hitTester = parentTool.hitTester;
    this.visualizer = parentTool.visualizer;
    this.mouse = parentTool.mouse;

    // Initialize services
    this.imageCapture = new ImageCapture(viewer, this.coordinateTransformer);
    this.lineDetectionService = new LineDetectionService(viewer, this.coordinateTransformer, config, this.imageCapture, parentTool.kindAdapter.polygonPipelineConfigPath ?? undefined, parentTool.kindAdapter.depthPipelineConfigPath ?? undefined);
    this.lineDetectionService.excludedWorldPolygonsProvider = () => this.parentTool.kindAdapter.getExcludedWorldPolygons?.(this.viewer, this.editingShape) ?? [];
    this.lineRenderer = new LineRenderer(viewer);
    this.shapeBuilder = new ShapeBuilder();
    this.shapeBuilder.setCoordinateTransformer(this.coordinateTransformer);

    // Initialize extracted modules
    this.promptAligner = new PromptAligner(this.coordinateTransformer);
    this.densitySnapper = new DensitySnapper(this.coordinateTransformer);
    this.boxExtrusionHandler = new BoxExtrusionHandler(this.coordinateTransformer, this.densitySnapper);
    this.shapeRendererModule = new LineShapeRenderer(viewer, this.planePreviewScene);
    this.promptDragHandler = new PromptDragHandler(this.coordinateTransformer, this.densitySnapper);
    this.deletionManager = new PromptDeletionManager(this.planePreviewScene, viewer.renderArea, this.visualizer, this.promptAligner, this.densitySnapper, () => ({
      densityMap: this.lineDetectionService.cachedDensityMap,
      positionMap: this.lineDetectionService.cachedPositionMap,
      floorHeight: this.currentBasePlane?.origin.z ?? null,
    }), this.coordinateTransformer);

    // Initialize area selector for drag box selection
    this.areaSelector = new AreaSelector(viewer, this.coordinateTransformer, {
      showLabel: true,
      showWarning: false,
      boxStyle: {
        border: '2px dashed white',
        background: 'rgba(100, 100, 100, 0.2)',
      },
    });

    this.customizeAreaSelectorLabel();

    // Depth segmentation support
    this.sparkleParticles = new SparkleParticleSystem(viewer, this.coordinateTransformer);

    // Shared constraint-snap indicator (dashed line + distance label).
    // No-op when the adapter doesn't opt into `enableConstraintSnap`.
    this.constraintSnapVisuals = new ConstraintSnapVisuals(viewer, this.planePreviewScene);

    // Radial edge-op menu (parallel / perpendicular / distance). Inert
    // until the user clicks (vs. drags) a prompt handle AND the active
    // adapter opts in via `enableEdgeOpMenu`. The host adapter reads
    // live state via arrow functions so the controller never holds
    // stale references to swapped-out prompt arrays.
    this.edgeOpController = new EdgeOpController(viewer, {
      getLinePrompts: () => this.linePrompts,
      getHitTester: () => this.hitTester,
      getVisualizer: () => this.visualizer,
      getOverlayScene: () => this.planePreviewScene,
      updateShapePreview: () => this.updateShapePreview(),
      pushHintOverride: (key: string) => this.parentTool.pushHintOverride(key),
      popHintOverride: () => this.parentTool.popHintOverride(),
      isShapeValid: () => {
        // Same predicate the drag flow uses to roll back invalid moves.
        const regular = this.linePrompts.filter((p) => !p.isTemporary);
        if (regular.length < 3) return regular.length === this.linePrompts.length;
        const shape = this.shapeBuilder.createShapeFromPrompts(regular);
        return !!shape && shape.vertices.length >= 3 && shape.vertices.length === regular.length;
      },
    });
  }

  activate(): void {
    this.parentTool.undoRedo?.clear();

    // Install the base-plane frame provider so every snap-module read
    // re-derives the frame from live state (associatedPlane, current
    // floor, editing shape). One-shot — no resync calls anywhere else.
    this.installBasePlaneFrameProvider();

    // Set up camera movement listeners
    this.viewer.addEventListener('camera_changed', this.handleCameraChange);
    this.viewer.addEventListener('controls_changed', this.handleCameraChange);

    // Add debug widget to overlay
    if (this.viewer.debugOverlay) {
      const widget = this.getDebugWidget();
      if (widget) {
        this.viewer.debugOverlay.addWidget(widget);
      }
    }

    // Listen for view changes to update projection plane for 3D mode
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleLineToolViewChanged);

    // Set up 3D mode projection plane if we have a floor
    this.updateProjectionPlane();

    // Initialize point size override from first point cloud and store originals
    this.initializePointSizeOverride();

    // Always run line detection on activate
    this.detectLines();

    this.parentTool.dispatchToolPhase('manual_idle');
  }

  deactivate(): void {
    // Clear any active hint override
    this.parentTool.popHintOverride();
    this.hoverOverrideId = null;

    // Tear down any in-flight edge-op (menu open, target-pick, or input).
    this.edgeOpController.cancel();

    // Remove camera listeners
    this.viewer.removeEventListener('camera_changed', this.handleCameraChange);
    this.viewer.removeEventListener('controls_changed', this.handleCameraChange);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleLineToolViewChanged);

    // Cancel area selection and two-click mode
    this.areaSelector.cancelSelection();
    this.dragBoxStart = null;
    this.rightDragStart = null;
    this.firstClickPoint = null;
    this.promptLineDrag = null;
    this.snappedBoxEdges = null;
    if (this.promptSphereDrag) {
      const { prompt, originalWorldPoint, originalDirection, originalPosLength, originalNegLength, neighborSnapshots } = this.promptSphereDrag;
      prompt.worldPoint.copy(originalWorldPoint);
      prompt.direction.copy(originalDirection);
      prompt.positiveLength = originalPosLength;
      prompt.negativeLength = originalNegLength;
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      for (const snap of neighborSnapshots) {
        snap.prompt.worldPoint.copy(snap.worldPoint);
        snap.prompt.screenPosition.copy(snap.screenPosition);
      }
      this.promptSphereDrag = null;
    }
    // Re-enable orbital controls (in case drag was active)
    this.viewer.controls.enabled = true;
    this.visualizer.clearLinePreview();
    this.visualizer.clearBoxPreview();
    this.constraintSnapVisuals.clear();

    // Clear all prompts on deactivation
    this.linePrompts = [];

    // If editing a room, cancel and restore original
    if (this.editingShape) {
      this.exitShapeEditMode(false);
    }

    // Clear prompt hover state and isolation plane
    this.hoveredPromptId = null;
    this.hoveredPreviewLineId = null;
    this.shiftDown = false;
    this.ctrlDown = false;
    this.parentTool.cancelPlaneRefinement();
    this.perpendicularSnapTarget = null;
    this.shiftLockedPromptId = null;
    this.visualizer.hideIsolationPlane();

    // Plane preview cleanup is handled by LineTool's onDisable

    // Clear projection plane in 3D mode
    this.clearProjectionPlane();

    // Restore original point sizes
    this.restoreOriginalPointSizes();

    // Remove debug widget from overlay and clean up debug visuals
    this.densitySnapper.debugEnabled = false;
    this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
    if (this.viewer.debugOverlay && this.debugWidget) {
      try {
        this.viewer.debugOverlay.removeWidget(this.debugWidget.name);
      } catch {
        // Widget already removed
      }
      this.debugWidget = null;
    }
    // Clear detection timeout
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
      this.cameraMoveTimeout = null;
    }

    // Cancel any ongoing detection by terminating worker
    this.lineDetectionService.cancel();
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear rendering
    this.lineRenderer.clear();
    this.clearShape();
    this.visualizer.clearPromptVisuals();

    // Stop sparkle particles
    this.sparkleParticles.dispose();

    // Dispose visualizer resources
    this.visualizer.dispose();
  }

  /**
   * Handle camera movement - cancel any ongoing detection and start a new one
   */
  private handleCameraChange = (): void => {
    // Check if camera actually moved by comparing position and target
    const camera = this.viewer.scene.getActiveCamera();
    const controls = this.viewer.controls;

    const currentPosition = camera.position.clone();
    const currentTarget = (controls as unknown as { getPivot?(): THREE.Vector3 })?.getPivot?.() ?? new THREE.Vector3();

    // If we have previous values, check if camera actually moved significantly
    // Use a threshold to ignore tiny floating-point drift from camera damping/easing
    const MOVEMENT_THRESHOLD = 1e-10;
    if (this.lastCameraPosition && this.lastCameraTarget) {
      const positionDelta = currentPosition.distanceToSquared(this.lastCameraPosition);
      const targetDelta = currentTarget.distanceToSquared(this.lastCameraTarget);

      if (positionDelta < MOVEMENT_THRESHOLD && targetDelta < MOVEMENT_THRESHOLD) {
        // Camera drift is below threshold, ignore this event
        return;
      }
    }

    // Update last known position/target
    this.lastCameraPosition = currentPosition;
    this.lastCameraTarget = currentTarget.clone();

    this.cameraMoving = true;

    // Cancel any ongoing detection immediately (also clears cachedPositionMap)
    this.lineDetectionService.cancel();

    // Clear detected lines - they are no longer valid for the new camera view
    this.detectedLines = [];
    this.hoveredPreviewLineId = null;
    this.lineRenderer.clear();
    this.updatePreviewLineHoverState(null);

    // Also abort the controller for any non-worker async operations
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear existing timeout
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
    }

    // Set new timeout to run line detection after camera stops moving
    this.cameraMoveTimeout = window.setTimeout(() => {
      this.cameraMoving = false;
      this.cameraMoveTimeout = null;

      // Always run line detection when camera stops
      this.detectLines();

      // Update shape preview with new lines if we have prompts
      if (this.linePrompts.length >= 3) {
        this.updateShapePreview();
      }
    }, this.detectionDebounceMs);

    // Update prompt screen positions for any hit testing
    this.updatePromptScreenPositions();

    // Update prompt visuals for screen-space dependent sizing in 2D mode
    if (this.linePrompts.length > 0) {
      this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
    }

    // Lines are now 3D objects in the scene, so they automatically stay in correct position
    // No need to update coordinates - just re-render to update colors/state if needed
    // Use safe render to validate corners before showing
    this.renderDetectedLinesSafe();
  };

  /**
   * Handle view changes - update projection plane and plane preview for 3D mode
   */
  private handleLineToolViewChanged = (): void => {
    this.updateProjectionPlane();
  };

  /**
   * Update the projection plane for 3D mode interactions
   * In 3D mode, we need a plane to project clicks onto (uses floor plane at floor height)
   * In 2D mode, the ViewerMode handles this automatically
   */
  private updateProjectionPlane(): void {
    const camera = this.viewer.scene.getActiveCamera();
    const is3DMode = camera.type === 'PerspectiveCamera';

    if (is3DMode && this.currentBasePlane) {
      // In 3D mode, create a projection plane at the base plane for accurate click projection
      const projectionPlane = new PointoramaPlane({
        position: this.currentBasePlane.origin.clone(),
        normal: this.currentBasePlane.normal.clone(),
        clipTask: ClipTask.NONE,
        tolerance: 0.01,
        finished: false,
        selected: false,
      });

      // Set as associated plane for coordinate transformer (for click projection)
      this.viewer.viewerMode.associatedPlane = projectionPlane;
      this.viewer.viewerMode.hasVirtualPlane = true;
    } else {
      // In 2D mode or when no floor is selected, clear our custom projection plane
      this.clearProjectionPlane();
    }
  }

  /**
   * Clear the projection plane set by this tool
   */
  private clearProjectionPlane(): void {
    // Only clear if we set it (check for virtual plane flag)
    if (this.viewer.viewerMode.hasVirtualPlane && !this.viewer.viewerMode.in2DMode) {
      this.viewer.viewerMode.associatedPlane = null;
      this.viewer.viewerMode.hasVirtualPlane = false;
    }
  }

  /**
   * Register a base-plane frame provider on the coordinate transformer.
   * The provider closure captures `this`, so every read by the snap
   * modules (ShapeBuilder, DensitySnapper, PromptDragHandler,
   * PromptAligner, BoxExtrusionHandler) re-derives the frame from live
   * state: whichever plane is currently installed on
   * `viewer.viewerMode.associatedPlane`, plus this mode's
   * `currentBasePlane` and `editingShape`. No cached frame exists, so
   * a swap of `associatedPlane` (e.g. DoorWindowTool re-focusing a
   * different wall) is picked up immediately by the next read. Called
   * once on activate; cleared via `CoordinateTransformer.clear()`
   * on tool disable.
   */
  protected installBasePlaneFrameProvider(): void {
    this.coordinateTransformer.setBasePlaneFrameProvider(() =>
      this.parentTool.kindAdapter.getBasePlaneFrame({
        viewer: this.viewer,
        basePlane: this.currentBasePlane,
        shape: this.editingShape,
      }),
    );
  }

  /**
   * Update projection plane when the base-plane selection changes
   */
  onBasePlaneChanged(basePlane: BasePlaneState | null): void {
    this.currentBasePlane = basePlane;
    // Update projection plane when the base plane changes
    this.updateProjectionPlane();
    // No frame resync needed — the provider reads `currentBasePlane` live.

    // Cancel any ongoing detection — cached maps are from the old floor
    this.lineDetectionService.cancel();
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    // Clear calculated results (detected lines from old floor)
    this.detectedLines = [];
    this.lineRenderer.clear();
    this.hoveredPreviewLineId = null;
    this.updatePreviewLineHoverState(null);
    this.clearShape();

    // Note: linePrompts are intentionally preserved across floor changes

    // Clear any pending camera-move timeout to avoid double-detection
    if (this.cameraMoveTimeout !== null) {
      window.clearTimeout(this.cameraMoveTimeout);
      this.cameraMoveTimeout = null;
    }

    // Re-trigger detection for the new floor after a delay
    // so the pointcloud has time to load for the new floor
    this.cameraMoveTimeout = window.setTimeout(() => {
      this.cameraMoveTimeout = null;
      this.detectLines();
      if (this.linePrompts.length >= 3) {
        this.updateShapePreview();
      }
    }, 1000);
  }

  /**
   * Get z-range from the floor isolation plane for position map capture.
   */
  private getZRange(): [number, number] | undefined {
    const range = this.currentBasePlane?.captureZRange;
    if (!range) return undefined;
    return [Math.min(range[0], range[1]), Math.max(range[0], range[1])];
  }

  /**
   * 8 oriented-box corners for the capture's eye-Z slab — see
   * `AnnotationKindAdapter.getCaptureBoxCorners`. Returns undefined for
   * rooms (the floor-isolation `zRange` is tight enough on a top-down
   * ortho); door/window returns the wall slab's 8 corners so the
   * position-map B channel actually spans the full wall-thickness range.
   */
  private getCaptureBoxCorners(): THREE.Vector3[] | undefined {
    const corners = this.parentTool.kindAdapter.getCaptureBoxCorners({
      viewer: this.viewer,
      basePlane: this.currentBasePlane,
      shape: this.editingShape,
    });
    return corners ?? undefined;
  }

  /**
   * Detect lines in the current view.
   * Uses AbortController to allow cancellation when camera moves.
   */
  private async detectLines(): Promise<void> {
    // Pure-geometry kinds (outline) opt out of segmentation entirely — no
    // viewport capture, no worker call, no sparkle preview.
    if (this.parentTool.kindAdapter.enableLineDetection === false) {
      this.detectedLines = [];
      this.lineRenderer.clear();
      this.visualizer.clearSnapTargets();
      this.sparkleParticles.stop();
      // Detection-less kinds may still need the viewport maps for density
      // snapping while dragging prompts (Wall Tracer room edits).
      if (this.parentTool.kindAdapter.captureMapsForSnapping) {
        await this.lineDetectionService.captureViewportMaps(this.getZRange(), this.getCaptureBoxCorners());
      }
      return;
    }

    // Cancel any previous detection
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
    }

    // Create new abort controller for this detection
    this.detectionAbortController = new AbortController();
    const signal = this.detectionAbortController.signal;

    this.isDetecting = true;
    this.lastDetectionTime = Date.now();

    // Capture both position and density maps via centralized service
    const captured = await this.lineDetectionService.captureViewportMaps(this.getZRange(), this.getCaptureBoxCorners());
    if (!captured) {
      this.isDetecting = false;
      return;
    }

    // Start sparkle particles to indicate processing
    this.sparkleParticles.start(captured.positionMap);

    try {
      const result = await this.lineDetectionService.detectLinesOnly(signal);

      // Check if aborted before updating state
      if (signal.aborted) {
        return;
      }

      // Check if results are still valid for current camera view
      if (!this.lineDetectionService.areLineDetectionResultsValid()) {
        this.sparkleParticles.stop();
        return; // Camera moved - results are stale
      }

      this.detectedLines = result.lines;

      // Render detected lines
      this.lineRenderer.render(this.detectedLines);

      // Stop sparkle particles after lines are rendered and visible
      this.sparkleParticles.stop();

      // Update debug widget with detection results
      this.updateDebugWidget();
    } catch (error) {
      // Ignore abort errors - they're expected when camera moves
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      if (error instanceof Error && error.message.includes('aborted')) {
        return;
      }
      console.error('[LineTool] Line detection failed:', error);
    } finally {
      this.isDetecting = false;
      // Update hint after detection completes based on current state
      if (this.editingShape) {
        this.parentTool.dispatchToolPhase('editing_room');
      } else if (this.linePrompts.length > 0) {
        // Don't override — updateShapePreview will dispatch the correct phase
      } else {
        this.parentTool.dispatchToolPhase('manual_idle');
      }
    }
  }

  /**
   * Enter shape edit mode: converts the shape's edges to prompts for editing.
   * Currently shape is a room; broadens to EditableShape in step B.
   */
  enterShapeEditMode(shape: EditableShape): void {
    this.parentTool.undoRedo?.clear();

    // Get edges from the shape (this also hides the shape's visualization)
    const edges = shape.enterEditMode();

    this.editingShape = shape;
    this.parentTool.dispatchToolPhase('editing_room');
    // No frame resync needed — the provider reads `editingShape` live.

    // Snap edge midpoints onto the kind's base plane before turning them
    // into prompts. Rooms: floor plane → flatten Z to floor height (the
    // prior behavior). Door/window: vertical wall plane → projection
    // preserves the wall-Z component. Pre-adapter this was a hardcoded
    // `wp.z = floorHeight` and broke vertical (door/window) edits.
    const snapped = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      edges.map((edge) => edge.midpoint.clone()),
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape },
    );
    this.linePrompts = edges.map((edge, i) => ({
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: snapped[i],
      direction: edge.direction.clone(),
      screenPosition: this.coordinateTransformer.worldToScreen(snapped[i]),
    }));

    // Update shape preview with the new prompts
    this.updateShapePreview();

    // Run line detection to capture maps for density snapping during edit
    this.detectLines();
  }

  /**
   * Exit room edit mode with optional confirmation
   * @param confirm If true, updates the room with the new shape. If false, restores original.
   */
  private exitShapeEditMode(confirm: boolean): void {
    if (!this.editingShape) {
      return;
    }

    if (confirm && this.shapeRendererModule.currentShape && this.shapeRendererModule.currentShape.vertices.length >= 3) {
      const newPolygon = this.parentTool.kindAdapter.snapPolygonToBasePlane(
        this.shapeRendererModule.currentShape.vertices.map((v) => v.clone()),
        { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape },
      );

      this.editingShape.exitEditMode(true, newPolygon);
      this.parentTool.kindAdapter.commitEdit(this.viewer, this.editingShape, newPolygon);
    } else {
      this.editingShape.exitEditMode(false);
    }

    this.parentTool.kindAdapter.restoreViewAfterShape(this.editingShape, this.viewer);

    this.editingShape = null;
    this.parentTool.undoRedo?.clear();
    this.clearSelection();
    this.parentTool.dispatchToolPhase('manual_idle');
  }

  isEditingShape(): boolean {
    return this.editingShape !== null;
  }

  getEditingShape(): EditableShape | null {
    return this.editingShape;
  }

  /** The shape should already be in edit mode (enterEditMode called). */
  setEditingShape(shape: EditableShape | null): void {
    this.editingShape = shape;
  }

  // ========== LineToolModeCallbacks ==========

  getNextPromptId(): string {
    return `prompt_${this.nextPromptId++}`;
  }

  isPromptSphereDragging(): boolean {
    return this.promptSphereDrag !== null;
  }

  addPrompt(prompt: LinePrompt): void {
    this.linePrompts.push(prompt);
  }

  /**
   * Handle click in 3D mode - create prompt from preview plane
   */
  /**
   * Customize the area selector label for deletion box
   */
  private customizeAreaSelectorLabel(): void {
    const label = this.areaSelector.getAreaLabel();
    if (label) {
      this.areaSelector.setUpdateAreaLabelOverride((mouse: THREE.Vector2) => {
        // Update label text to show deletion info
        const selectionStart = this.areaSelector.getSelectionStart();
        if (selectionStart && label) {
          if (this.rightDragStart) {
            // Right-drag: deletion mode - use deletion manager for highlighting and validation
            // skipGuards=true: manual mode always allows deletion, showing warnings instead
            const { selectedPrompts, validation } = this.deletionManager.updateSelectionHighlight(this.linePrompts, selectionStart, mouse, this.hoveredPromptId, true);

            const count = selectedPrompts.length;
            if (count > 0 && validation.warning) {
              // Deletion will break the shape - show warning in yellow
              label.textContent = validation.warning;
              label.style.backgroundColor = 'rgba(200, 150, 0, 0.8)';
            } else {
              // Valid deletion or no prompts - show nothing
              label.textContent = '';
              label.style.backgroundColor = 'transparent';
            }
          }
        }
      });
    }
  }

  /**
   * Rectangle-only kinds (door/window) lock prompt insertion/deletion once
   * the shape's four sides exist — post-segmentation prompts or an edited
   * shape — because extra/missing vertices would break the rectangle. Hand
   * drawing the FIRST four sides stays possible (< 4 prompts, not editing).
   */
  private rectangleLocked(): boolean {
    if (this.parentTool.kindAdapter.rectangleOnly !== true) return false;
    return this.editingShape !== null || this.linePrompts.length >= 4;
  }

  /**
   * Delete all line prompts whose worldPoint (midpoint/sphere) falls inside a screen-space box.
   * Implements smart deletion: when consecutive prompts are removed, connects the boundary
   * prompts with a new edge to maintain a valid shape.
   */
  private deletePromptsInBox(start: THREE.Vector2, end: THREE.Vector2): void {
    if (this.rectangleLocked()) return;
    // Snapshot before deletion so Ctrl+Z restores the pre-delete state
    this.parentTool.pushUndoSnapshot();

    // Use deletion manager to execute the delete with validation
    // skipGuards=true: manual mode always allows deletion, showing warnings instead
    const newPrompts = this.deletionManager.executeDelete(this.linePrompts, start, end, this.hoveredPromptId, true, (prompts) => {
      this.linePrompts = prompts;
      this.updateShapePreview();
    });

    if (newPrompts === null) {
      // Deletion was invalid or no prompts selected — pop the snapshot we just pushed
      this.parentTool.undoRedo?.popState();
      return;
    }

    this.linePrompts = newPrompts;

    // The shape will automatically reconnect through the ShapeBuilder when we call updateShapePreview
    this.updateShapePreview();
  }

  // ─── Undo / Redo (LineToolModeUndoRedo interface) ─────────────────────────

  captureUndoSnapshot(): PromptSnapshot {
    return {
      prompts: this.linePrompts.map((p) => ({
        id: p.id,
        worldPoint: p.worldPoint.clone(),
        direction: p.direction.clone(),
        screenPosition: p.screenPosition.clone(),
        positiveLength: p.positiveLength,
        negativeLength: p.negativeLength,
      })),
      nextPromptId: this.nextPromptId,
    };
  }

  restoreFromSnapshot(snapshot: PromptSnapshot): void {
    this.linePrompts = snapshot.prompts.map((s) => ({
      id: s.id,
      worldPoint: s.worldPoint.clone(),
      direction: s.direction.clone(),
      screenPosition: s.screenPosition.clone(),
      positiveLength: s.positiveLength,
      negativeLength: s.negativeLength,
    }));
    this.nextPromptId = snapshot.nextPromptId;

    this.updatePromptScreenPositions();
    this.updateShapePreview();

    if (this.linePrompts.length === 0) {
      this.clearShape();
      this.visualizer.clearPromptVisuals();
      this.lineRenderer.render(this.detectedLines);
    }
  }

  prepareForUndoRedo(): void {
    this.firstClickPoint = null;
    this.visualizer.clearLinePreview();
  }

  // ─── Prompt sphere drag helpers (delegated to PromptDragHandler / DensitySnapper) ──

  private movePromptToMouse(mouse: THREE.Vector2): void {
    if (!this.promptSphereDrag) return;
    this.lastDragMouse = mouse.clone();
    const floorHeight = this.currentBasePlane?.origin.z ?? null;
    const camera = this.viewer.scene.getActiveCamera();
    const viewportWidth = this.viewer.renderArea.clientWidth;
    const viewportHeight = this.viewer.renderArea.clientHeight;

    // Call movePromptToMouse with detected lines for snap-to-line support.
    // `invertShiftFor90Snap` flips the shift bit for door/window where
    // 90° lock is the natural default. Rooms keep shift = lock.
    //
    // Pure-geometry kinds (outline) opt out of detection entirely — pass
    // empty/null maps so the drag handler skips snap-to-line + density
    // alignment, and don't render the detected-line snap targets afterward.
    // Kinds with `captureMapsForSnapping` keep the density maps flowing
    // (density snapping without detection — Wall Tracer room edits).
    const detectionOn = this.parentTool.kindAdapter.enableLineDetection !== false;
    const snapMapsOn = detectionOn || this.parentTool.kindAdapter.captureMapsForSnapping === true;
    const effectiveShift = this.parentTool.kindAdapter.invertShiftFor90Snap ? !this.shiftDown : this.shiftDown;
    const snapResult = this.promptDragHandler.movePromptToMouse(
      mouse,
      this.promptSphereDrag,
      this.linePrompts,
      floorHeight,
      snapMapsOn ? this.lineDetectionService.cachedDensityMap : null,
      snapMapsOn ? this.lineDetectionService.cachedPositionMap : null,
      effectiveShift,
      () => this.updateShapePreview(),
      detectionOn ? this.detectedLines : [],
      viewportWidth,
      viewportHeight,
      camera,
      this.ctrlDown,
      { perpendicularOnly: this.parentTool.kindAdapter.rectangleOnly === true },
    );

    // Optional adapter-level constraint snap (shared with AutomaticLineMode).
    // Off by default; adapters opt in via `enableConstraintSnap`. Runs after
    // the drag handler so detected-line snap remains priority for kinds
    // that use it.
    if (applyConstraintSnap(this.promptSphereDrag.prompt, {
      viewer: this.viewer,
      kindAdapter: this.parentTool.kindAdapter,
      basePlane: this.currentBasePlane,
      editingShape: this.editingShape,
      coordinateTransformer: this.coordinateTransformer,
      modifiers: { shiftDown: this.shiftDown, ctrlDown: this.ctrlDown },
      allPrompts: this.linePrompts,
      visuals: this.constraintSnapVisuals,
    })) {
      this.updateShapePreview();
    }

    // Render snap target spheres on detected lines, highlighting the nearest one.
    // Skipped when the kind has detection disabled (outline edits).
    if (detectionOn) {
      const nearestLineId = snapResult?.line.id ?? null;
      this.visualizer.renderSnapTargets(this.detectedLines, nearestLineId);
      if (this.densitySnapper.debugEnabled) {
        this.densitySnapper.renderDebug(this.viewer.scene.sceneDebug, floorHeight);
      } else {
        this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
      }
    } else {
      this.visualizer.clearSnapTargets();
      this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
    }
  }

  private cleanupEdgesAfterDrag(movedPrompt: LinePrompt): void {
    // Rectangle-only kinds keep fixed 90° corners — the collinear merge could
    // only ever fire erroneously and would destroy a side, so skip it.
    if (this.parentTool.kindAdapter.rectangleOnly) return;
    const floorHeight = this.currentBasePlane?.origin.z ?? null;
    this.linePrompts = this.promptDragHandler.cleanupEdgesAfterDrag(movedPrompt, this.linePrompts, floorHeight, (prompts) => {
      this.linePrompts = prompts;
      this.updateShapePreview();
    });
  }

  /**
   * Handle right-click to cancel two-click mode or remove prompts
   */
  onRightClick(event: MouseEvent): void {
    // Skip if a right-drag box just completed (contextmenu fires after mouseup)
    if (this.rightDragWasBox) {
      this.rightDragWasBox = false;
      return;
    }

    // Ctrl-refine right-click is handled by LineTool before delegation

    // Cancel prompt sphere drag — restore original position + neighbors
    if (this.promptSphereDrag) {
      // Discard the undo snapshot that was pushed at drag start
      this.parentTool.undoRedo?.popState();
      const { prompt, originalWorldPoint, originalDirection, originalPosLength, originalNegLength, neighborSnapshots } = this.promptSphereDrag;
      prompt.worldPoint.copy(originalWorldPoint);
      prompt.direction.copy(originalDirection);
      prompt.positiveLength = originalPosLength;
      prompt.negativeLength = originalNegLength;
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      for (const snap of neighborSnapshots) {
        snap.prompt.worldPoint.copy(snap.worldPoint);
        snap.prompt.screenPosition.copy(snap.screenPosition);
      }
      this.promptSphereDrag = null;
      this.dragBoxStart = null;
      this.visualizer.clearSnapTargets();
      this.constraintSnapVisuals.clear();
      this.updateShapePreview();
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel prompt line drag (box creation) if active
    if (this.promptLineDrag) {
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.dragBoxStart = null;
      this.visualizer.clearBoxPreview();
      this.viewer.controls.enabled = true;
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel two-click mode if active
    if (this.firstClickPoint) {
      this.firstClickPoint = null;
      this.visualizer.clearLinePreview();
      this.showPreviewLines();
      this.viewer.renderer.domElement.style.cursor = 'default';
      return;
    }

    // Cancel drag box if active
    if (this.areaSelector.isSelecting()) {
      this.areaSelector.cancelSelection();
      this.dragBoxStart = null;
      return;
    }

    // Don't process while camera is moving
    if (this.cameraMoving || this.cameraMoveTimeout !== null) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    const camera = this.viewer.scene.getActiveCamera();
    const is3D = camera.type === 'PerspectiveCamera';

    if (is3D) {
      // In 3D, right-click deletes a hovered prompt, otherwise undoes
      const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
      if (hoveredPrompt) {
        this.parentTool.pushUndoSnapshot();
        const index = this.linePrompts.indexOf(hoveredPrompt);
        if (index !== -1) {
          this.linePrompts.splice(index, 1);
          this.updateShapePreview();
        }
      } else {
        this.parentTool.undoRedo?.undo();
      }
      return;
    }

    // If no prompts, nothing to remove
    if (this.linePrompts.length === 0) return;

    // Find the closest prompt to the click position
    const closestPrompt = this.hitTester.findClosestPrompt(mouse, this.linePrompts);

    if (closestPrompt) {
      this.parentTool.pushUndoSnapshot();
      // Remove the prompt
      const index = this.linePrompts.indexOf(closestPrompt);
      if (index !== -1) {
        this.linePrompts.splice(index, 1);

        // Always update shape preview (updates connection points and prompts visuals)
        this.updateShapePreview();
      }
    }
  }

  /**
   * Update hover state for prompts and show isolation plane
   */
  private updatePromptHoverState(screenPoint: THREE.Vector2): void {
    const hoveredPrompt = this.hitTester.findHoveredPromptSphere(screenPoint, this.linePrompts);
    const newHoveredId = hoveredPrompt?.id || null;

    if (newHoveredId !== this.hoveredPromptId) {
      this.hoveredPromptId = newHoveredId;
    }
  }

  /**
   * Update isolation plane visibility for preview line hover
   */
  private updatePreviewLineHoverState(hoveredLine: DetectedLine | null): void {
    const is3D = this.viewer.scene.getActiveCamera().type === 'PerspectiveCamera';

    // Isolation plane only in 3D — in x-ray it covers the whole floor
    if (hoveredLine && is3D) {
      const midpoint = this.lineRenderer.getLineMidpoint(hoveredLine);
      const floorHeight = this.currentBasePlane?.origin.z;
      this.visualizer.showIsolationPlane(midpoint, hoveredLine.direction, floorHeight);
    } else if (!this.hoveredPromptId) {
      this.visualizer.hideIsolationPlane();
    }
  }

  /**
   * Show preview lines again (detected lines) - used when exiting manual drawing mode
   */
  private showPreviewLines(): void {
    // Unhide all lines and re-render
    for (const line of this.detectedLines) {
      line.isHidden = false;
    }
    this.renderDetectedLinesSafe();
  }

  /**
   * Safely render detected lines - validates camera corners match before rendering.
   * If corners don't match, clears the lines instead of showing stale data.
   */
  private renderDetectedLinesSafe(): void {
    // Validate that line detection results are still valid for current camera view
    if (!this.lineDetectionService.areLineDetectionResultsValid()) {
      // Lines are stale - clear them
      this.detectedLines = [];
      this.lineRenderer.clear();
      return;
    }
    this.lineRenderer.render(this.detectedLines);
  }

  /**
   * Get mouse position relative to renderArea
   */
  private getMousePosition(event: MouseEvent): THREE.Vector2 | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top);
  }

  /**
   * Handle line click - create a persistent prompt at the click location
   */
  private handleLineClick(line: DetectedLine, event: MouseEvent): void {
    // Get click position in screen space
    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    this.parentTool.pushUndoSnapshot();

    // Project click position onto line segment in world space
    // Use the line's world coordinates directly (more accurate than screenToWorld)
    const worldPoint = this.hitTester.closestPointOnLineFromScreen(line, mouse);

    // Calculate lengths in positive and negative directions
    // The direction vector points from start to end, so:
    // - positive direction goes towards line.end
    // - negative direction goes towards line.start
    const toStart = new THREE.Vector3().subVectors(line.start, worldPoint);
    const toEnd = new THREE.Vector3().subVectors(line.end, worldPoint);

    // Project onto direction to get signed distances
    const distToStartAlongDir = toStart.dot(line.direction); // negative value (towards start)
    const distToEndAlongDir = toEnd.dot(line.direction); // positive value (towards end)

    // positiveLength = distance in positive direction (towards end)
    // negativeLength = distance in negative direction (towards start, stored as positive value)
    const positiveLength = Math.max(0, distToEndAlongDir);
    const negativeLength = Math.max(0, -distToStartAlongDir);

    // Create persistent LinePrompt with world position, direction, and lengths
    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint,
      direction: line.direction.clone(),
      screenPosition: mouse.clone(),
      positiveLength,
      negativeLength,
    };
    this.linePrompts.push(prompt);

    // Always update shape preview (updates connection points and prompts visuals)
    this.updateShapePreview();
  }

  /**
   * Create a prompt perpendicular to the target prompt line.
   * The new prompt is placed at the midpoint between cursor and snap point,
   * with direction perpendicular to the target line.
   * The combined edge lengths equal the distance from cursor to the line.
   */
  private createPerpendicularSnapPrompt(): void {
    if (!this.perpendicularSnapTarget) return;

    this.parentTool.pushUndoSnapshot();

    const { snapPoint, previewEndPoint, distance } = this.perpendicularSnapTarget;

    // Calculate perpendicular direction (from snap point to cursor position)
    const perpendicularDir = new THREE.Vector3().subVectors(previewEndPoint, snapPoint).normalize();

    // Place the prompt at the midpoint between snap point and cursor
    const midpoint = new THREE.Vector3().addVectors(snapPoint, previewEndPoint).multiplyScalar(0.5);

    // The combined edge lengths should equal the distance from cursor to line
    // Since prompt is at midpoint, each direction extends half the distance
    const halfDistance = distance / 2;

    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: midpoint,
      direction: perpendicularDir,
      screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
      positiveLength: halfDistance,
      negativeLength: halfDistance,
    };
    this.linePrompts.push(prompt);

    // Clear perpendicular snap state
    this.perpendicularSnapTarget = null;
    this.visualizer.clearLinePreview();

    // Update shape preview
    this.updateShapePreview();
  }

  /**
   * Create a line prompt from two manually clicked points
   */
  private createLinePromptFromTwoPoints(point1: THREE.Vector3, point2: THREE.Vector3): void {
    this.parentTool.pushUndoSnapshot();

    // Calculate direction from point1 to point2
    const direction = new THREE.Vector3().subVectors(point2, point1).normalize();

    // Use midpoint as the prompt location
    const midpoint = new THREE.Vector3().addVectors(point1, point2).multiplyScalar(0.5);

    // Snap midpoint onto the kind's base plane. `screenToWorld` already
    // projects onto the active plane, but accumulated noise can leave the
    // midpoint a fraction off — and for some kinds it may sit on the
    // far-clip plane instead of the intended base plane. Rooms: floor
    // plane → flatten Z to floor height (the prior behaviour). Door/
    // window: vertical wall plane → projection preserves the wall-Z
    // component, so a diagonal/L-shaped prompt stays diagonal. Pre-
    // adapter this was a hardcoded `midpoint.z = floorHeight` and broke
    // vertical (door/window) edits — all prompts collapsed to floor Z
    // and the shape came out horizontal regardless of input.
    const [snappedMidpoint] = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      [midpoint],
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape },
    );
    midpoint.copy(snappedMidpoint);

    // Calculate half-length (distance from midpoint to each end is the same)
    const halfLength = point1.distanceTo(point2) / 2;

    // Create prompt with equal lengths in both directions
    const prompt: LinePrompt = {
      id: `prompt_${this.nextPromptId++}`,
      worldPoint: midpoint,
      direction,
      screenPosition: this.coordinateTransformer.worldToScreen(midpoint),
      positiveLength: halfLength,
      negativeLength: halfLength,
    };
    this.linePrompts.push(prompt);

    // Update shape preview
    this.updateShapePreview();
  }

  /**
   * Snap a raw `screenToWorld` result onto the active base plane.
   *
   * `screenToWorld` is supposed to return a point already on the plane, but
   * in base-plane-aligned x-ray views its ray/plane intersection can miss and
   * fall through to the `z=0` unproject fallback — yielding a point with a
   * wildly wrong Z. The line geometry is created via `snapPolygonToBasePlane`
   * so the committed line still lands correctly, but the *preview* used the
   * raw point directly, so its far endpoint shot off-screen and only the start
   * sphere ("a dot") remained visible. Routing preview endpoints through the
   * same adapter snap restores them — this is the per-orientation replacement
   * for the old hardcoded `point.z = startPoint.z` lock that the XY→base-plane
   * refactor removed.
   */
  private snapPreviewPointToBasePlane(point: THREE.Vector3): THREE.Vector3 {
    const [snapped] = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      [point],
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape: this.editingShape },
    );
    return snapped;
  }

  /**
   * Update the line preview visualization from first click point to current mouse position
   */
  private updateLinePreview(mouse: THREE.Vector2): void {
    if (!this.firstClickPoint) {
      this.visualizer.clearLinePreview();
      return;
    }

    const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (currentWorldPoint) {
      this.visualizer.renderLinePreview(this.firstClickPoint, this.snapPreviewPointToBasePlane(currentWorldPoint));
    }
  }

  /**
   * Create a box (3 new prompts) from dragging off a prompt line, and split the original prompt.
   * The box consists of:
   * - Corner 1: drag start point (fixed)
   * - Corner 4: mouse release position (diagonal from corner 1)
   * - Corner 2 & 3: calculated to form a rectangle aligned with prompt direction
   * The original prompt is split at the drag start point.
   */
  private createBoxFromPromptLineDrag(drag: { prompt: LinePrompt; startPoint: THREE.Vector3; side: 'positive' | 'negative' }, endWorldPoint: THREE.Vector3, snappedEdges?: Array<[THREE.Vector3, THREE.Vector3]> | null): void {
    // Check if we currently have a valid shape (before the box operation)
    const regularPromptsBefore = this.linePrompts.filter((p) => !p.isTemporary);
    const shapeBefore = regularPromptsBefore.length >= 3 ? this.shapeBuilder.createShapeFromPrompts(regularPromptsBefore) : null;
    const hadValidShape = shapeBefore !== null && shapeBefore.vertices.length >= 3 && shapeBefore.vertices.length === regularPromptsBefore.length;

    // Deep snapshot: createBoxFromDrag may mutate neighbor prompts in-place
    // (via adjustNeighborForCollapsedLeg), so we need to save their mutable state
    const promptsBefore = [...this.linePrompts];
    const promptSnapshots = this.linePrompts.map((p) => ({
      prompt: p,
      worldPoint: p.worldPoint.clone(),
      screenPosition: p.screenPosition.clone(),
    }));
    const savedNextPromptId = this.nextPromptId;

    const result = this.boxExtrusionHandler.createBoxFromDrag(
      drag,
      endWorldPoint,
      this.linePrompts,
      () => `prompt_${this.nextPromptId++}`,
      snappedEdges ?? null,
      this.lineDetectionService.cachedDensityMap,
      this.lineDetectionService.cachedPositionMap,
      this.currentBasePlane?.origin.z ?? null,
    );
    if (!result) return;

    if (result.originalPromptIndex !== -1) {
      this.linePrompts.splice(result.originalPromptIndex, 1);
    }
    this.linePrompts.push(...result.newPrompts);

    // If we had a valid shape before, verify the box didn't break it
    const allowInvalid = this.debugWidget?.allowInvalidBoxShape ?? false;
    if (hadValidShape && !drag.prompt.isTemporary && !allowInvalid) {
      const regularPromptsAfter = this.linePrompts.filter((p) => !p.isTemporary);
      const shapeAfter = this.shapeBuilder.createShapeFromPrompts(regularPromptsAfter);
      const isStillValid = shapeAfter !== null && shapeAfter.vertices.length >= 3 && shapeAfter.vertices.length === regularPromptsAfter.length;

      if (!isStillValid) {
        // Revert array and restore mutated prompt positions
        this.linePrompts = promptsBefore;
        this.nextPromptId = savedNextPromptId;
        for (const snapshot of promptSnapshots) {
          snapshot.prompt.worldPoint.copy(snapshot.worldPoint);
          snapshot.prompt.screenPosition.copy(snapshot.screenPosition);
        }
        this.updateShapePreview();

        this.viewer.scene.dispatchEvent({
          type: 'box_breaks_shape_notification',
        });
        return;
      }
    }

    this.updateShapePreview();
  }

  /**
   * Update screen positions of all prompts (called on camera move)
   */
  private updatePromptScreenPositions(): void {
    for (const prompt of this.linePrompts) {
      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
    }
  }

  /**
   * Update shape preview from line prompts using ray intersection algorithm.
   * Always updates connection points on prompts, even if no valid shape exists yet.
   */
  updateShapePreview(): void {
    this.shapeRendererModule.clear();

    // Trigger line detection when first prompt is added (editing mode starts)
    // This switches from segmentation mode to line detection mode
    if (this.linePrompts.length === 1) {
      this.detectLines();
    }

    // Use the new ray-based algorithm with line prompts
    // This will update connection points on all prompts
    const shape = this.shapeBuilder.createShapeFromPrompts(this.linePrompts);

    // Update prompt visuals to reflect new connection points.
    // Handles render in the viewer UI scene, so they draw on top of the shape.
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);

    if (shape && shape.vertices.length >= 3) {
      this.shapeRendererModule.render(shape);
      if (!this.editingShape) {
        this.parentTool.dispatchToolPhase('editing_shape');
      }
    } else if (this.linePrompts.length > 0) {
      this.parentTool.dispatchToolPhase('manual_idle');
    }
  }

  private clearShape(): void {
    this.shapeRendererModule.clear();
  }

  /**
   * Finalize shape and convert to room
   */
  private finalizeShape(): void {
    this.parentTool.undoRedo?.clear();
    if (!this.shapeRendererModule.currentShape) {
      console.warn('[LineTool] No shape to finalize');
      return;
    }

    if (this.shapeRendererModule.currentShape.vertices.length < 3) {
      console.warn('[LineTool] Not enough vertices to create room');
      return;
    }

    // Check if we have a base plane
    if (!this.currentBasePlane) {
      console.warn('[LineTool] No floor plane selected, cannot create room');
      return;
    }

    this.createRoomFromShape(this.shapeRendererModule.currentShape);
  }

  /**
   * Materialize the drawn shape via the kind adapter's `commitNew`. The
   * adapter owns event-name + payload shape (rooms vs door/windows vs …).
   */
  private createRoomFromShape(shape: LineShape): void {
    // Only a valid base plane is required to commit. `captureZRange` is a
    // room-specific capture hint (floor+ceiling slab) and is intentionally null
    // for surfaces, so it must NOT gate the commit.
    if (!this.currentBasePlane) {
      console.warn('[LineTool] Cannot create shape without a base plane');
      return;
    }

    const polygonPoints = this.parentTool.kindAdapter.snapPolygonToBasePlane(
      shape.vertices.map((v) => v.clone()),
      { viewer: this.viewer, basePlane: this.currentBasePlane, shape: null },
    );

    this.parentTool.kindAdapter.commitNew(this.viewer, polygonPoints, {
      basePlane: this.currentBasePlane,
    });

    this.clearSelection();
    setTimeout(() => this.detectLines(), 50);
  }

  /**
   * Clear current selection and all prompts
   */
  private clearSelection(): void {
    if (this.linePrompts.length > 0) {
      this.parentTool.pushUndoSnapshot();
    }
    this.linePrompts = [];
    this.firstClickPoint = null;
    this.visualizer.clearLinePreview();
    this.clearShape();
    this.visualizer.clearPromptVisuals();
    this.lineRenderer.render(this.detectedLines);

    this.parentTool.dispatchToolPhase('manual_idle');
  }

  /**
   * Handle keyboard shortcuts
   */
  onKeyDown(event: KeyboardEvent): void {
    // Undo/Redo is handled by BaseTool (LineTool shell) via keyboard interception

    // Edge-op controller owns its own Esc when it's running a pick/input
    // phase. Let it consume first so the mode's normal Esc handling
    // doesn't fight the menu.
    if (this.edgeOpController.isActive() && this.edgeOpController.onKeyDown(event)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape') {
      // Ctrl-refine Escape is handled by LineTool before delegation
      // First cancel two-click mode if active
      if (this.firstClickPoint) {
        this.firstClickPoint = null;
        this.visualizer.clearLinePreview();
        this.showPreviewLines();
        this.viewer.renderer.domElement.style.cursor = 'default';
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // If editing a room, cancel and restore original
      if (this.editingShape) {
        this.exitShapeEditMode(false);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Then clear prompts if any exist (before exiting the tool)
      if (this.linePrompts.length > 0) {
        this.clearSelection();
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // If nothing to cancel, let escape propagate (to exit tool)
    } else if (event.key === 'Enter') {
      // If editing a room, confirm the changes
      if (this.editingShape && this.linePrompts.length >= 3) {
        this.exitShapeEditMode(true);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      // Otherwise create a new room from the shape
      if (this.linePrompts.length >= 3) {
        this.finalizeShape();
      }
    } else if (event.key === 'r' || event.key === 'R') {
      // Refresh detection
      this.detectLines();
    } else if (event.key === 'Shift') {
      this.shiftDown = true;
      this.updatePreviewLinesForShift();
      if (this.linePrompts.length > 0) {
        this.parentTool.pushHintOverride('shift_snap');
      }
      // Re-run drag calculation with rotation enabled
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    } else if (event.key === 'Control' && !event.repeat) {
      // Refinement start is handled by LineTool before delegation
      this.ctrlDown = true;
      if (!this.parentTool.isRefining) {
        this.updatePreviewLinesForCtrl();
        if (this.promptSphereDrag) {
          this.parentTool.pushHintOverride('ctrl_no_snap');
        }
      }
      if (this.promptSphereDrag) {
        this.viewer.controls.enabled = false;
        if (this.lastDragMouse) {
          this.movePromptToMouse(this.lastDragMouse);
        }
      }
    }
  }

  /**
   * Handle key up events
   */
  onKeyUp(event: KeyboardEvent): void {
    // Edge-op controller wants Shift-release to commit when it's been
    // collecting targets. Forward before normal handling.
    if (this.edgeOpController.isActive()) {
      this.edgeOpController.onKeyUp(event);
    }
    if (event.key === 'Shift') {
      this.shiftDown = false;
      this.updatePreviewLinesForShift();
      this.parentTool.popHintOverride();
      // Re-run drag calculation without rotation
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    } else if (event.key === 'Control') {
      this.ctrlDown = false;
      // Refinement finalization is handled by LineTool before delegation
      if (!this.parentTool.isRefining) {
        this.updatePreviewLinesForCtrl();
        this.parentTool.popHintOverride();
      }
      this.viewer.controls.enabled = true;
      if (this.promptSphereDrag && this.lastDragMouse) {
        this.movePromptToMouse(this.lastDragMouse);
      }
    }
  }

  /**
   * Update preview lines visibility based on shift state.
   * When prompts exist: Shift toggles perpendicular snap mode.
   * When no prompts: Shift hides preview lines (manual drawing mode).
   */
  private updatePreviewLinesForShift(): void {
    // When prompts exist: Shift enables perpendicular snap mode
    if (this.linePrompts.length > 0) {
      if (this.shiftDown) {
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.visualizer.hideIsolationPlane();
        this.renderDetectedLinesSafe();
      } else {
        this.perpendicularSnapTarget = null;
        this.shiftLockedPromptId = null;
        this.visualizer.clearLinePreview();
        // Check if lines are valid when exiting perpendicular mode
        if (!this.lineDetectionService.areLineDetectionResultsValid() || this.detectedLines.length === 0) {
          this.detectLines();
        } else {
          for (const line of this.detectedLines) {
            line.isHidden = false;
          }
          this.renderDetectedLinesSafe();
        }
        // Re-render prompts without shift highlight
        this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
      }
      return;
    }

    // No prompts: original shift behavior (hide preview lines for manual drawing)
    // If we're in two-click mode (first point placed), stay in manual mode regardless of shift state
    if (this.firstClickPoint) {
      // Keep lines hidden while in two-click mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return;
    }

    // If we're in drag mode (drag start point set), stay in manual mode regardless of shift state
    if (this.dragStartWorldPoint) {
      // Keep lines hidden while in drag mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return;
    }

    if (this.shiftDown) {
      // Shift pressed: hide all preview lines for manual drawing mode
      for (const line of this.detectedLines) {
        line.isHovered = false;
        line.isHidden = true;
      }
      // Hide isolation plane when entering manual mode
      this.visualizer.hideIsolationPlane();
    } else {
      // Shift released: show all preview lines again
      for (const line of this.detectedLines) {
        line.isHidden = false;
        // isHovered will be updated on next mouse move
      }
    }

    this.lineRenderer.render(this.detectedLines);
  }

  /**
   * Update preview lines visibility based on control key state.
   * In ManualLineMode, Ctrl has no special effect on preview lines
   * (perpendicular snap is now on Shift).
   */
  private updatePreviewLinesForCtrl(): void {
    // Ctrl has no effect on line visibility in ManualLineMode
  }

  /**
   * Update the perpendicular snap preview during mouse move when shift is held.
   * Draws a preview line from the cursor to the closest point on a prompt line.
   */
  private updatePerpendicularSnapPreview(mouse: THREE.Vector2): void {
    // Get world position from mouse
    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) {
      this.perpendicularSnapTarget = null;
      this.visualizer.clearLinePreview();
      return;
    }

    // Check if hovering over a prompt sphere or line — lock it as snap target
    const hoveredPromptSphere = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
    if (hoveredPromptSphere) {
      this.shiftLockedPromptId = hoveredPromptSphere.id;
    } else {
      const hoveredPromptLine = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (hoveredPromptLine) {
        this.shiftLockedPromptId = hoveredPromptLine.prompt.id;
      }
    }

    // Determine which prompt to snap to: locked prompt or closest
    const lockedPrompt = this.shiftLockedPromptId ? this.linePrompts.find((p) => p.id === this.shiftLockedPromptId) : undefined;

    const snapTarget = this.hitTester.findPerpendicularSnapTarget(worldPoint, this.linePrompts, lockedPrompt);
    if (!snapTarget) {
      this.perpendicularSnapTarget = null;
      this.visualizer.clearLinePreview();
      return;
    }

    // Store the snap target for click handling
    this.perpendicularSnapTarget = {
      prompt: snapTarget.prompt,
      snapPoint: snapTarget.snapPoint,
      previewEndPoint: worldPoint,
      distance: snapTarget.distance,
    };

    // Set Z height of snap point to match
    this.perpendicularSnapTarget.snapPoint.z = worldPoint.z;

    // Draw the preview line from cursor to snap point
    this.drawPerpendicularPreviewLine(worldPoint, this.perpendicularSnapTarget.snapPoint);

    // Update prompt visuals with shift-highlight on the snap target prompt
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId, null, this.shiftLockedPromptId);
  }

  /**
   * Draw a preview line for perpendicular snap (from cursor to snap point on prompt line)
   */
  private drawPerpendicularPreviewLine(fromPoint: THREE.Vector3, toPoint: THREE.Vector3): void {
    this.visualizer.renderLinePreview(fromPoint, toPoint);
  }

  /**
   * Handle mouse move for hover detection and plane preview
   */
  onMouseMove(event: MouseEvent): void {
    // Mouse position and plane preview are updated by LineTool before delegation

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Edge-op controller owns hover during a pick phase so it can light up
    // the prompt/wall under the cursor as a target affordance.
    if (this.edgeOpController.isActive()) {
      this.edgeOpController.onMouseMove(event, mouse);
      return;
    }

    // Get camera mode
    const camera = this.viewer.scene.getActiveCamera();
    const is2DMode = camera.type === 'OrthographicCamera';

    // Handle right-drag deletion box
    if (this.rightDragStart && is2DMode) {
      const dragDistance = mouse.distanceTo(this.rightDragStart);
      if (dragDistance > 5) {
        if (!this.areaSelector.isSelecting()) {
          this.areaSelector.startSelection(this.rightDragStart);
        }
        this.areaSelector.updateSelection(mouse);
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
      }
      return;
    }

    // Handle prompt sphere drag (move prompt to follow mouse, snap via pixel alignment)
    if (this.promptSphereDrag && is2DMode) {
      const now = performance.now();
      if (now - this.promptSphereDrag.lastUpdateTime >= 16) {
        this.promptSphereDrag.lastUpdateTime = now;
        this.movePromptToMouse(mouse);
      }
      this.viewer.renderer.domElement.style.cursor = 'grabbing';
      return;
    }

    // Handle perpendicular snap mode (shift key held with existing prompts)
    if (this.shiftDown && is2DMode && this.linePrompts.length > 0) {
      this.updatePerpendicularSnapPreview(mouse);
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      return; // Don't process other hover effects during perpendicular snap mode
    }

    // Handle box preview during drag from prompt line (2D mode)
    if (is2DMode && this.promptLineDrag && this.dragBoxStart) {
      const promptLineDrag = this.promptLineDrag;
      const dragDistance = mouse.distanceTo(this.dragBoxStart);
      if (dragDistance > 5) {
        // Get current world position. `screenToWorld` already on the
        // active base plane (floor for rooms, wall plane for door/window
        // phase-2); no Z-lock needed.
        const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
        if (currentWorldPoint) {

          // When drag started from an intersection, switch between the two meeting
          // edges using the angle bisector as the decision boundary
          if (this.promptLineDrag.snappedToEndpoint && this.promptLineDrag.connectedPrompt) {
            const dragVec = new THREE.Vector3().subVectors(currentWorldPoint, this.promptLineDrag.startPoint);
            // Get directions pointing AWAY from the intersection along each prompt
            const currentPrompt = this.promptLineDrag.prompt;
            const connectedPrompt = this.promptLineDrag.connectedPrompt;
            const currentAway = currentPrompt.direction.clone().normalize();
            const connectedAway = connectedPrompt.direction.clone().normalize();
            // Flip if the intersection is at the positive end (direction points toward it)
            // We want the direction pointing away from the intersection into the prompt body
            const currentPosPoint = currentPrompt.connections?.positive?.point;
            if (currentPosPoint && currentPosPoint.distanceTo(this.promptLineDrag.startPoint) < 0.01) {
              currentAway.negate();
            }
            const connectedPosPoint = connectedPrompt.connections?.positive?.point;
            if (connectedPosPoint && connectedPosPoint.distanceTo(this.promptLineDrag.startPoint) < 0.01) {
              connectedAway.negate();
            }
            // The bisector has equal angles to both away-directions
            const bisector = currentAway.clone().add(connectedAway).normalize();
            // Normal to the bisector, in the active base plane — separates
            // the two sides. Pre-fix this was a hardcoded XY 90° rotation
            // (`(-bisector.y, bisector.x, 0)`), which collapses any
            // vertical component to zero on wall-plane edits — every
            // diagonal bisector resolved to the same horizontal normal
            // and the side test broke.
            const bisectorNormal = this.boxExtrusionHandler.inPlanePerp(bisector);
            // Determine which side of the bisector the mouse is on
            const dragSide = dragVec.dot(bisectorNormal);
            const currentSide = currentAway.dot(bisectorNormal);
            // If the mouse is on the opposite side from the current prompt's away dir, switch
            if (Math.sign(dragSide) !== Math.sign(currentSide) && Math.abs(dragSide) > 0.001) {
              this.promptLineDrag.prompt = connectedPrompt;
              this.promptLineDrag.connectedPrompt = currentPrompt;
            }
          }

          // Calculate clamped box corners (clamped to prompt line endpoints)
          const box = this.boxExtrusionHandler.computeBoxCorners(this.promptLineDrag.startPoint, currentWorldPoint, this.promptLineDrag.prompt);

          if (box) {
            const { corner1, corner2, corner3, corner4, promptDir, perpDir, sideLength, perpLength } = box;
            this.visualizer.renderBoxPreview([corner1, corner2, corner3, corner4]);

            // Compute density-snapped green preview (skip when Ctrl held)
            const densityMap = this.lineDetectionService.cachedDensityMap;
            const positionMap = this.lineDetectionService.cachedPositionMap;
            const floorHeight = this.currentBasePlane?.origin.z ?? null;

            if (!this.ctrlDown && densityMap) {
              this.snappedBoxEdges = this.boxExtrusionHandler.computeSnappedBoxEdges(corner1, corner2, corner3, corner4, promptDir, perpDir, sideLength, perpLength, densityMap, positionMap, floorHeight, promptLineDrag.prompt);
              if (this.snappedBoxEdges) {
                this.visualizer.renderSnappedBoxPreview(this.snappedBoxEdges);
              }
            } else {
              this.snappedBoxEdges = null;
              this.visualizer.clearSnappedBoxPreview();
            }
          }

          this.viewer.renderer.domElement.style.cursor = 'crosshair';
          return;
        }
      }
    }

    // Update line preview if we have a first click point (two-click mode in 2D)
    if (this.firstClickPoint && is2DMode) {
      this.updateLinePreview(mouse);
      // Show crosshair cursor during two-click mode
      this.viewer.renderer.domElement.style.cursor = 'crosshair';
      // Keep preview lines hidden during two-click mode
      for (const line of this.detectedLines) {
        line.isHidden = true;
      }
      this.lineRenderer.render(this.detectedLines);
      return; // Don't process other hover effects during two-click mode
    }

    // Update line preview during drag-to-create-line (2D mode)
    if (is2DMode && this.dragStartWorldPoint && this.dragBoxStart) {
      const dragDistance = mouse.distanceTo(this.dragBoxStart);
      if (dragDistance > 5) {
        // Show preview line from drag start to current position. Snap the live
        // endpoint onto the base plane (see `snapPreviewPointToBasePlane`) so it
        // stays visible in base-plane-aligned x-ray views.
        const currentWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
        if (currentWorldPoint) {
          this.visualizer.renderLinePreview(this.dragStartWorldPoint, this.snapPreviewPointToBasePlane(currentWorldPoint));
        }
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        // Keep preview lines hidden during drag
        for (const line of this.detectedLines) {
          line.isHidden = true;
        }
        this.lineRenderer.render(this.detectedLines);
        return; // Don't process other hover effects during drag
      }
    }

    // Don't process hover while camera is moving or detection is pending
    if (this.cameraMoving || this.cameraMoveTimeout !== null) return;

    // During Ctrl-refine, skip hover override management
    if (this.parentTool.isRefining) return;

    // Update prompt sphere hover state (works in both 2D and 3D modes)
    this.updatePromptHoverState(mouse);

    // Check if hovering over a prompt sphere - show grab cursor (draggable)
    const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
    if (hoveredPrompt) {
      this.viewer.renderer.domElement.style.cursor = is2DMode ? 'grab' : 'pointer';
      // Clear preview line hover state when hovering over placed prompt
      this.updatePreviewLineHoverState(null);
      // In 3D, hide the plane circle and hover preview so only the existing prompt is visible
      if (!is2DMode) {
        this.parentTool.activePlane.visible = false;
        this.parentTool.activePlane.unHighlight();
        this.visualizer.clearHoverPreview();
        if (this.hoverOverrideId !== 'right_click_delete_single') {
          this.hoverOverrideId = 'right_click_delete_single';
          this.parentTool.pushHintOverride('right_click_delete_single');
        }
      } else {
        if (this.hoverOverrideId !== 'drag_move') {
          this.hoverOverrideId = 'drag_move';
          this.parentTool.pushHintOverride('drag_move');
        }
      }
      return; // Don't process line hover if hovering over a prompt sphere
    }

    // Check if hovering over a prompt direction line - show crosshair cursor
    if (is2DMode && this.linePrompts.length > 0) {
      const promptLineHit = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (promptLineHit) {
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        this.updatePreviewLineHoverState(null);
        if (this.hoverOverrideId !== 'wall_snap_drag') {
          this.hoverOverrideId = 'wall_snap_drag';
          this.parentTool.pushHintOverride('wall_snap_drag');
        }
        return;
      }
    }

    // Clear hover override if we were hovering but no longer are
    if (this.hoverOverrideId !== null) {
      this.hoverOverrideId = null;
      this.parentTool.popHintOverride();
    }

    // In 3D mode, check for editable-shape hover (pointer cursor, hide plane preview/visualizer)
    if (!is2DMode && !this.editingShape && this.linePrompts.length === 0) {
      if (this.parentTool.kindAdapter.isMouseOverEditableShape({ viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane })) {
        this.viewer.renderer.domElement.style.cursor = 'pointer';
        this.parentTool.activePlane.visible = false;
        this.parentTool.activePlane.unHighlight();
        this.visualizer.clearHoverPreview();
        return;
      }
    }

    // Line hover detection only in 2D mode
    if (is2DMode) {
      // When shift is held AND we have prompts, hide all preview lines (manual drawing mode)
      if (this.shiftDown && this.linePrompts.length > 0) {
        // Hide all preview lines
        for (const line of this.detectedLines) {
          line.isHovered = false;
          line.isHidden = true;
        }
        this.lineRenderer.render(this.detectedLines);
        this.updatePreviewLineHoverState(null);
        this.viewer.renderer.domElement.style.cursor = 'crosshair';
        return;
      }

      // Editable-shape hover (only when not editing and no prompts placed) —
      // adapter decides whether the cursor target counts (room, strip, …).
      if (!this.editingShape && this.linePrompts.length === 0) {
        if (this.parentTool.kindAdapter.isMouseOverEditableShape({ viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane })) {
          this.viewer.renderer.domElement.style.cursor = 'pointer';
          this.updatePreviewLineHoverState(null);
          return;
        }
      }

      // Lines are always visible and hoverable
      const hoveredLine = this.lineRenderer.getLineAtPosition(mouse.x, mouse.y, this.detectedLines);

      this.hoveredPreviewLineId = hoveredLine?.id || null;

      for (const line of this.detectedLines) {
        line.isHovered = line === hoveredLine;
        line.isHidden = false;
      }

      this.updatePreviewLineHoverState(hoveredLine);
      this.lineRenderer.render(this.detectedLines);
      this.viewer.renderer.domElement.style.cursor = hoveredLine ? 'pointer' : 'default';
    } else {
      // In 3D mode, reset cursor if not hovering over prompt
      this.updatePreviewLineHoverState(null);
      this.viewer.renderer.domElement.style.cursor = 'default';
    }
  }

  /**
   * Handle mouse down to start drag box or place prompts
   */
  onMouseDown(event: MouseEvent): void {
    const camera = this.viewer.scene.getActiveCamera();
    const is2DMode = camera.type === 'OrthographicCamera';

    if (!is2DMode) return; // only 2D logic is handled here right now, return early to prevent incorrect behavior

    // While the edge-op controller is in a pick-target / input phase, it
    // owns left-clicks (target selection). Defer to it before normal handling.
    if (this.edgeOpController.isActive()) {
      const mouse = this.getMousePosition(event);
      if (mouse && this.edgeOpController.onMouseDown(event, mouse)) return;
    }

    // Right mouse button: start right-drag for prompt deletion box
    if (event.button === 2) {
      const mouse = this.getMousePosition(event);
      if (mouse && this.linePrompts.length > 0) {
        this.rightDragStart = mouse.clone();
        this.parentTool.pushHintOverride('right_click_delete');
      }
      return;
    }

    // Only handle left mouse button
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Track mouse down position for drag detection
    this.dragBoxStart = mouse.clone();

    // In 2D mode, check if starting drag from a prompt's direction line
    if (is2DMode && this.linePrompts.length > 0 && !this.firstClickPoint) {
      // Check if mouse is on a prompt's direction line (not the center sphere)
      const promptLineHit = this.hitTester.findPromptLineAtPosition(mouse, this.linePrompts);
      if (promptLineHit) {
        // Start prompt line drag for box creation
        this.promptLineDrag = {
          prompt: promptLineHit.prompt,
          startPoint: promptLineHit.worldPoint.clone(),
          side: promptLineHit.side,
          snappedToEndpoint: promptLineHit.snappedToEndpoint,
        };
        // When snapped to an intersection, find the connected prompt for edge switching.
        // Use geometric search rather than connections (which can be stale for temp prompts).
        if (promptLineHit.snappedToEndpoint) {
          const neighbor = this.boxExtrusionHandler.findNeighborAtEndpoint(promptLineHit.worldPoint, promptLineHit.prompt, this.linePrompts);
          if (neighbor) {
            this.promptLineDrag.connectedPrompt = neighbor;
          }
        }
        // Disable orbital controls during box drag
        this.viewer.controls.enabled = false;
        this.parentTool.pushHintOverride('wall_snap_drag');
        return;
      }
    }

    // In 2D mode, capture world position for potential line creation on ANY click
    if (is2DMode && !this.firstClickPoint) {
      // Get world position for potential drag start (line creation). Snap onto
      // the base plane so a stray `screenToWorld` fallback Z doesn't corrupt
      // either the drag preview or the created line (see
      // `snapPreviewPointToBasePlane`).
      const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (worldPoint) {
        this.dragStartWorldPoint = this.snapPreviewPointToBasePlane(worldPoint);
      }
    }

    // In 2D mode with prompts, check if clicking on a prompt sphere (drag to move)
    if (is2DMode && this.linePrompts.length > 0 && !this.firstClickPoint && !this.promptLineDrag) {
      const hoveredPrompt = this.hitTester.findHoveredPromptSphere(mouse, this.linePrompts);
      if (hoveredPrompt) {
        // Snapshot neighbor positions for cancel support
        const neighborSnapshots: Array<{
          prompt: LinePrompt;
          worldPoint: THREE.Vector3;
          screenPosition: THREE.Vector2;
          farPrompt: LinePrompt | null;
          side?: 'pos' | 'neg';
        }> = [];
        const hovConns = hoveredPrompt.connections;
        if (hovConns) {
          const posConn: PromptConnection | undefined = hovConns.positive;
          const negConn: PromptConnection | undefined = hovConns.negative;
          const connSides: Array<{ conn: PromptConnection | undefined; side: 'pos' | 'neg' }> = [
            { conn: posConn, side: 'pos' },
            { conn: negConn, side: 'neg' },
          ];
          for (const { conn, side } of connSides) {
            if (!conn) continue;
            const neighborIdx = conn.targetPromptIndex;
            if (neighborIdx >= 0 && neighborIdx < this.linePrompts.length) {
              const neighborPrompt = this.linePrompts[neighborIdx];
              if (neighborPrompt !== hoveredPrompt) {
                // Only slide neighbors of the same kind:
                // - Temp prompts only slide other temp prompts
                // - Regular prompts only slide other regular prompts
                if (!!hoveredPrompt.isTemporary !== !!neighborPrompt.isTemporary) continue;

                // Find the far-side prompt (neighbor's other connection, away from dragged prompt)
                let farPrompt: LinePrompt | null = null;
                const nPos: PromptConnection | undefined = neighborPrompt.connections?.positive;
                const nNeg: PromptConnection | undefined = neighborPrompt.connections?.negative;
                for (const nConn of [nPos, nNeg]) {
                  if (!nConn) continue;
                  const farIdx: number = nConn.targetPromptIndex;
                  if (farIdx >= 0 && farIdx < this.linePrompts.length) {
                    const farCandidate: LinePrompt = this.linePrompts[farIdx];
                    if (farCandidate !== hoveredPrompt && farCandidate !== neighborPrompt) {
                      farPrompt = farCandidate;
                      break;
                    }
                  }
                }
                neighborSnapshots.push({
                  prompt: neighborPrompt,
                  worldPoint: neighborPrompt.worldPoint.clone(),
                  screenPosition: neighborPrompt.screenPosition.clone(),
                  farPrompt,
                  side,
                });
              }
            }
          }
        }

        // Snapshot before any drag mutations so Ctrl+Z restores the pre-drag state
        this.parentTool.pushUndoSnapshot();

        this.promptSphereDrag = {
          prompt: hoveredPrompt,
          originalWorldPoint: hoveredPrompt.worldPoint.clone(),
          originalDirection: hoveredPrompt.direction.clone(),
          originalPosLength: hoveredPrompt.positiveLength,
          originalNegLength: hoveredPrompt.negativeLength,
          lastUpdateTime: 0,
          lastFittedDirection: null,
          neighborSnapshots,
        };
        // Disable orbital controls during prompt drag
        this.viewer.controls.enabled = false;
        this.viewer.renderer.domElement.style.cursor = 'grabbing';
        this.parentTool.pushHintOverride('drag_move');
      }
    }
  }

  /**
   * Handle mouse up to complete drag box or place prompts
   */
  onMouseUp(event: MouseEvent): void {
    // Right mouse button: complete deletion drag box
    if (event.button === 2) {
      if (this.rightDragStart && this.areaSelector.isSelecting()) {
        const mouse = this.getMousePosition(event);
        if (mouse) {
          this.deletePromptsInBox(this.rightDragStart, mouse);
        }
        this.areaSelector.cancelSelection();
        this.rightDragWasBox = true;
      }
      // Clear deletion highlighting
      this.deletionManager.clearHighlighting(this.linePrompts, this.hoveredPromptId);
      this.rightDragStart = null;
      this.viewer.renderer.domElement.style.cursor = 'default';
      this.parentTool.popHintOverride();
      return;
    }

    // Only handle left mouse button
    if (event.button !== 0) return;

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Handle prompt sphere drag completion. If the cursor barely moved
    // (< 5px), treat this as a CLICK on the handle instead — restore the
    // pre-drag snapshot and (when the kind opts in) open the radial
    // edge-op menu. The drag init unconditionally sets up state on
    // mousedown, so we have to unwind it here.
    if (this.promptSphereDrag) {
      const CLICK_THRESHOLD_PX = 5;
      const movedFromStart = this.dragBoxStart ? mouse.distanceTo(this.dragBoxStart) : Infinity;
      const wasClick = movedFromStart <= CLICK_THRESHOLD_PX;
      const drag = this.promptSphereDrag;

      if (wasClick) {
        // Restore the pre-drag snapshot so the prompt's own state is
        // pristine before we open the menu.
        drag.prompt.worldPoint.copy(drag.originalWorldPoint);
        drag.prompt.direction.copy(drag.originalDirection);
        drag.prompt.positiveLength = drag.originalPosLength;
        drag.prompt.negativeLength = drag.originalNegLength;
        for (const snap of drag.neighborSnapshots) {
          snap.prompt.worldPoint.copy(snap.worldPoint);
          snap.prompt.screenPosition.copy(snap.screenPosition);
        }
        // Discard the undo snapshot that was pushed at drag init — nothing
        // changed visually so there's nothing to undo.
        this.parentTool.undoRedo?.popState();
        this.updateShapePreview();

        this.promptSphereDrag = null;
        this.lastDragMouse = null;
        this.dragBoxStart = null;
        this.viewer.controls.enabled = true;
        this.visualizer.clearSnapTargets();
        this.constraintSnapVisuals.clear();
        this.viewer.renderer.domElement.style.cursor = 'default';
        this.parentTool.popHintOverride();

        if (this.parentTool.kindAdapter.enableEdgeOpMenu) {
          this.edgeOpController.openMenuFor(drag.prompt);
        }
        return;
      }

      this.movePromptToMouse(mouse);
      this.cleanupEdgesAfterDrag(drag.prompt);
      this.promptSphereDrag = null;
      this.lastDragMouse = null;
      this.dragBoxStart = null;
      // Re-enable orbital controls after drag ends (in case ctrl was held)
      this.viewer.controls.enabled = true;
      this.visualizer.clearSnapTargets();
      this.constraintSnapVisuals.clear();
      this.viewer.renderer.domElement.style.cursor = 'default';
      this.parentTool.popHintOverride();
      return;
    }

    const camera = this.viewer.scene.getActiveCamera();
    const is3DMode = camera.type === 'PerspectiveCamera';

    // Check if this was a drag (mouse moved significantly from start)
    const dragThreshold = 5; // pixels
    const wasDrag = this.dragBoxStart && mouse.distanceTo(this.dragBoxStart) > dragThreshold;

    // In 3D mode, click handling is done by LineTool.onClick (BaseTool filters drags)
    if (is3DMode) return;

    // 2D mode below

    // Handle box creation from prompt line drag
    if (wasDrag && this.promptLineDrag) {
      const endWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (endWorldPoint) {
        this.parentTool.pushUndoSnapshot();
        // `screenToWorld` already on the active base plane; no Z-lock.

        // When ctrl is held, snappedBoxEdges is null. Build raw edges from the
        // purple preview corners so the final result matches exactly (no density snap).
        let edges = this.snappedBoxEdges;
        if (!edges) {
          const box = this.boxExtrusionHandler.computeBoxCorners(this.promptLineDrag.startPoint, endWorldPoint, this.promptLineDrag.prompt);
          if (box) {
            edges = [
              [box.corner2.clone(), box.corner4.clone()],
              [box.corner4.clone(), box.corner3.clone()],
              [box.corner3.clone(), box.corner1.clone()],
            ];
          }
        }

        this.createBoxFromPromptLineDrag(this.promptLineDrag, endWorldPoint, edges);
      }
      // Clear drag state
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.dragBoxStart = null;
      this.visualizer.clearBoxPreview();
      this.viewer.controls.enabled = true;
      this.parentTool.popHintOverride();
      return;
    }

    // Clear prompt line drag state if it was just a click (not a drag)
    if (this.promptLineDrag) {
      this.promptLineDrag = null;
      this.snappedBoxEdges = null;
      this.viewer.controls.enabled = true;
      this.parentTool.popHintOverride();
    }

    // Handle drag-to-create-line: if we have a drag start point, complete the line
    if (wasDrag && this.dragStartWorldPoint && !this.firstClickPoint) {
      // Rectangle-only lock: no additional sides once the rectangle exists.
      if (this.rectangleLocked()) {
        this.dragBoxStart = null;
        this.dragStartWorldPoint = null;
        this.visualizer.clearLinePreview();
        return;
      }
      // Get world position for drag end. `screenToWorld` already on the
      // active base plane; no Z-lock needed.
      const endWorldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (endWorldPoint) {

        // Create line prompt from the drag start to drag end
        this.createLinePromptFromTwoPoints(this.dragStartWorldPoint, endWorldPoint);

        // Clear drag state
        this.dragBoxStart = null;
        this.dragStartWorldPoint = null;
        this.visualizer.clearLinePreview();
        this.showPreviewLines();
        return;
      }
    }

    // If we have a drag start but didn't actually drag (no area selector active),
    // this is a click - handle prompt placement
    if (this.dragBoxStart) {
      this.dragBoxStart = null;
      this.dragStartWorldPoint = null;
      // In 2D mode, handle line selection or manual line creation
      this.handle2DLineClick(event);
    }
  }

  /**
   * Handle click to select lines and place prompts
   * In 2D mode: Click on detected lines
   * In 3D mode: Click to create prompt from preview plane position
   */
  onClick(_event: MouseEvent): void {
    // Click handling is now done in onMouseUp for better drag detection
  }

  /**
   * Handle click in 2D mode - detected line selection, room editing, or manual line creation
   */
  private handle2DLineClick(event: MouseEvent): void {
    // Don't allow clicks while camera is moving or detection is pending
    if (this.cameraMoving || this.cameraMoveTimeout !== null) {
      return;
    }

    const mouse = this.getMousePosition(event);
    if (!mouse) return;

    // Get world position for the click
    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) return;

    // Rectangle-only lock: once the rectangle's sides exist, clicks may only
    // enter edit mode (via the hit-test below) — no new prompts or lines.
    const rectLocked = this.rectangleLocked();
    if (rectLocked) this.firstClickPoint = null;

    // Handle perpendicular snap mode (shift key held)
    if (!rectLocked && this.shiftDown && this.perpendicularSnapTarget) {
      this.createPerpendicularSnapPrompt();
      return;
    }

    // Check if we have a first click point (two-click mode)
    if (this.firstClickPoint) {
      // Second click - create line prompt from two points
      this.createLinePromptFromTwoPoints(this.firstClickPoint, worldPoint);
      this.firstClickPoint = null;
      this.visualizer.clearLinePreview();
      this.showPreviewLines();
      return;
    }

    // If not already editing a shape and no prompts placed, route through
    // the kind adapter's hit-test so 2D and 3D click paths agree.
    if (!this.editingShape && this.linePrompts.length === 0) {
      const adapterCtx = { viewer: this.viewer, persona: this.persona, mouse: this.parentTool.mouse, coordinateTransformer: this.coordinateTransformer, basePlane: this.currentBasePlane };
      const clickedShape = this.parentTool.kindAdapter.hitTestEditableTarget(adapterCtx);
      if (clickedShape) {
        this.parentTool.kindAdapter.configureViewForShape(clickedShape, this.viewer);
        this.enterShapeEditMode(clickedShape);
        return;
      }
    }

    // Rectangle-only lock: nothing below may run — every remaining branch
    // creates a prompt or starts a new line.
    if (rectLocked) return;

    // Check if clicking on a detected line
    const clickedLine = this.lineRenderer.getLineAtPosition(mouse.x, mouse.y, this.detectedLines);

    if (clickedLine) {
      // Clicking on detected line - create prompt at click position
      this.handleLineClick(clickedLine, event);
    } else {
      // Clicking empty space - start two-click mode to create a free line.
      // Snap onto the base plane (see `snapPreviewPointToBasePlane`) so the
      // preview and the created line stay valid in x-ray views.
      this.firstClickPoint = this.snapPreviewPointToBasePlane(worldPoint.clone());
      this.updateLinePreview(mouse);
    }
  }

  /**
   * Handle shift + scroll wheel (placeholder - no brush size to adjust without segmentation)
   */
  onWheel(_event: WheelEvent): void {
    // No-op: brush size adjustment removed with segmentation
  }

  /**
   * Get debug widget for line detection visualization
   */
  getDebugWidget(): LineDetectionDebugWidget | null {
    if (!this.debugWidget) {
      this.debugWidget = new LineDetectionDebugWidget();

      // Set up unit converters for displaying distances in cm
      this.debugWidget.setUnitConverters(
        (pixels: number) => this.coordinateTransformer.pixelsToCm(pixels),
        (cm: number) => this.coordinateTransformer.cmToPixels(cm),
      );

      // Set up line detection pipeline with the debug widget
      this.debugWidget.setPipeline(this.lineDetectionService.getPipeline(), () => {
        // Re-run line detection when pipeline changes
        this.detectLines();
      });

      // Set up depth segmentation pipeline tab
      this.debugWidget.setDepthPipeline(this.lineDetectionService.getDepthPipeline(), () => {
        // Sync changes to worker then re-run detection to apply new depth config
        this.lineDetectionService.updateDepthPipelineConfig().then(() => {
          this.detectLines();
        });
      });

      // Set up polygon processing pipeline tab
      this.debugWidget.setPolygonPipeline(this.lineDetectionService.getPolygonPipeline(), () => {
        // Sync changes to worker - no polygon processing without segmentation
        this.lineDetectionService.updatePolygonPipelineConfig();
      });

      // Set up callback for spawning debug textures in world
      this.debugWidget.setSpawnTextureCallback((type, imageData) => {
        this.spawnDebugTextureInWorld(type, imageData);
      });

      // Set up callback for clearing debug textures from world
      this.debugWidget.setClearTextureCallback((type) => {
        this.clearDebugTexturesFromWorld(type);
      });

      // Set up density snap debug toggle
      this.debugWidget.setDensitySnapDebugCallback((enabled) => {
        this.densitySnapper.debugEnabled = enabled;
        if (!enabled) {
          this.densitySnapper.clearDebug(this.viewer.scene.sceneDebug);
        }
      });

      // Set up callback for getting line prompts (copy to clipboard / export fixture)
      this.debugWidget.setGetLinePromptsCallback(() => {
        return this.linePrompts.map((p) => ({
          id: p.id,
          worldPoint: { x: p.worldPoint.x, y: p.worldPoint.y, z: p.worldPoint.z },
          direction: { x: p.direction.x, y: p.direction.y, z: p.direction.z },
          positiveLength: p.positiveLength,
          negativeLength: p.negativeLength,
          isTemporary: p.isTemporary,
        }));
      });

      // Set up callback for setting line prompts (paste from clipboard)
      this.debugWidget.setSetLinePromptsCallback((prompts) => {
        this.parentTool.pushUndoSnapshot();
        this.linePrompts = prompts.map((p) => ({
          id: p.id,
          worldPoint: new THREE.Vector3(p.worldPoint.x, p.worldPoint.y, p.worldPoint.z),
          direction: new THREE.Vector3(p.direction.x, p.direction.y, p.direction.z),
          screenPosition: this.coordinateTransformer.worldToScreen(new THREE.Vector3(p.worldPoint.x, p.worldPoint.y, p.worldPoint.z)),
          positiveLength: p.positiveLength,
          negativeLength: p.negativeLength,
          isTemporary: p.isTemporary,
        }));
        this.updateShapePreview();
      });
    }
    return this.debugWidget;
  }

  /**
   * Update debug widget with latest detection data
   */
  private updateDebugWidget(): void {
    if (!this.debugWidget) {
      return;
    }

    // Update position map visualization
    this.debugWidget.setPositionMap(this.lineDetectionService.cachedPositionMap);

    // Update density map visualization
    this.debugWidget.setDensityMap(this.lineDetectionService.cachedDensityMap);

    // Update statistics if detection data is available
    const debugData = this.lineDetectionService.lastDebugData;
    if (debugData) {
      this.debugWidget.setStats({
        detectionTime: debugData.detectionTime,
        linesDetected: debugData.rawLines.length,
        linesAfterFiltering: debugData.filteredLines.length,
        imageSize: {
          width: debugData.capturedImage.width,
          height: debugData.capturedImage.height,
        },
        config: {
          minConfidence: this.lineDetectionService['config'].minConfidence,
          minLineLength: this.lineDetectionService['config'].minLineLength,
          houghThreshold: this.lineDetectionService['config'].houghThreshold,
        },
      });
    }

    // Update segmentation visualization
    this.debugWidget.setSegmentationData(this.lineDetectionService.lastSegmentationColoredData, this.lineDetectionService.lastSegmentationWidth, this.lineDetectionService.lastSegmentationHeight);

    // Refresh pipeline panel to update cm/pixel conversions
    this.debugWidget.refreshPipelinePanel();
  }

  /**
   * Spawn a debug texture as a plane in the 3D world.
   * Uses the current mask corners from the coordinate transformer for positioning.
   * The plane is positioned at the associated plane's Z level (room floor).
   */
  private spawnDebugTextureInWorld(type: 'position-map' | 'density-map' | 'segmentation', imageData: ImageData): void {
    const debugTexturePlanes = this.viewer.debugTexturePlanes;
    if (!debugTexturePlanes) {
      console.warn('[LineTool] Debug texture planes manager not available');
      return;
    }

    const maskCorners = this.coordinateTransformer.getMaskCorners();
    if (maskCorners.length !== 4) {
      console.warn('[LineTool] No mask corners available for spawning texture plane');
      return;
    }

    // Get the room's Z level from the associated plane (floor level)
    const associatedPlane = this.viewer.viewerMode.associatedPlane;
    let roomZ: number | null = null;
    if (associatedPlane) {
      roomZ = associatedPlane.position.z;
    }

    // Adjust corners to use room Z level if available
    let adjustedCorners = maskCorners;
    if (roomZ !== null) {
      const z = roomZ;
      adjustedCorners = maskCorners.map((corner) => {
        const adjusted = corner.clone();
        adjusted.z = z;
        return adjusted;
      });
    }

    // Spawn the texture plane at the adjusted corners
    const planeId = debugTexturePlanes.createPlaneFromImageData(imageData, adjustedCorners, {
      type: type,
      label: type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      opacity: 0.8,
      zOffset: 0.02, // Slight offset to prevent z-fighting with point cloud
    });

    console.log(`[LineTool] Spawned debug texture plane: ${planeId}`);
  }

  /**
   * Clear all debug texture planes of a specific type from the 3D world.
   */
  private clearDebugTexturesFromWorld(type: 'position-map' | 'density-map' | 'segmentation'): void {
    const debugTexturePlanes = this.viewer.debugTexturePlanes;
    if (!debugTexturePlanes) {
      console.warn('[LineTool] Debug texture planes manager not available');
      return;
    }

    const count = debugTexturePlanes.clearPlanesByType(type);
    console.log(`[LineTool] Cleared ${count} debug texture plane(s) of type: ${type}`);
  }

  /**
   * Initialize point size override from first point cloud
   * Stores original point sizes and applies the persisted override value
   */
  private initializePointSizeOverride(): void {
    const pointclouds = this.viewer.scene.pointclouds;
    if (pointclouds.length === 0) return;

    // Only initialize the setting value from point cloud on first activation
    if (!this.pointSizeInitialized) {
      const firstPointCloud = pointclouds[0];
      if (firstPointCloud.material && firstPointCloud.material.uniforms.size) {
        this.settings.temporaryPointSize = Number(firstPointCloud.material.uniforms.size.value);
      }
      this.pointSizeInitialized = true;
    }

    // Store original point sizes for all point clouds (always do this on enable)
    for (const pointcloud of pointclouds) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        this.originalPointSizes.set(pointcloud, Number(pointcloud.material.uniforms.size.value));
      }
    }

    // Apply the persisted override value
    this.applyPointSizeOverride(this.settings.temporaryPointSize);
  }

  /**
   * Apply temporary point size override to all point clouds
   */
  private applyPointSizeOverride(pointSize: number): void {
    const pointclouds = this.viewer.scene.pointclouds;
    for (const pointcloud of pointclouds) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        pointcloud.material.uniforms.size.value = pointSize;
      }
    }
  }

  /**
   * Restore original point sizes when tool exits
   */
  private restoreOriginalPointSizes(): void {
    for (const [pointcloud, originalSize] of this.originalPointSizes) {
      if (pointcloud.material && pointcloud.material.uniforms.size) {
        pointcloud.material.uniforms.size.value = originalSize;
      }
    }
    this.originalPointSizes.clear();
  }

  /**
   * Handle settings changes from the settings panel
   * Called by the parent LineTool shell when settings change
   */
  onSettingsChange(event: ToolSettingsChangeEvent): void {
    const { key, value } = event;

    // Update settings
    if (key === 'temporaryPointSize') {
      this.settings.temporaryPointSize = value as number;
      this.applyPointSizeOverride(this.settings.temporaryPointSize);
    } else if (key === 'enableCustomSegmentIsolation') {
      this.settings.enableCustomSegmentIsolation = value as boolean;
    } else if (key === 'segmentIsolationTolerance') {
      this.settings.segmentIsolationTolerance = value as number;
    } else if (key === 'segmentIsolationHeight') {
      this.settings.segmentIsolationHeight = value as number;
    }

    // Note: Floor isolation is handled by the LineTool shell
    // The shell will re-apply isolation and pass the updated plane to us
  }

  /**
   * Get the current line prompts (for mode switching)
   */
  getLinePrompts(): LinePrompt[] {
    return this.linePrompts;
  }

  /**
   * Clear the detected-lines rendering (green suggestion lines) without
   * deactivating the mode. Hosts that only borrow this tool for room-edit
   * sessions (Wall Tracer) call this when a session ends — the suggestions
   * must not linger once another tool owns the canvas again.
   */
  clearDetectionVisuals(): void {
    this.lineRenderer.clear();
    this.detectedLines = [];
    this.sparkleParticles.stop();
  }

  /**
   * Public detection refresh (same as the 'r' shortcut). For kinds with
   * detection disabled but `captureMapsForSnapping` set, this recaptures
   * just the snap maps — hosts call it when they inject prompts without
   * going through enterShapeEditMode (Wall Tracer draft/test sessions).
   */
  refreshDetection(): void {
    void this.detectLines();
  }

  /**
   * Set line prompts (for mode switching)
   * Also updates the shape preview and visuals
   */
  setLinePrompts(prompts: LinePrompt[]): void {
    this.linePrompts = prompts.map((p) => ({
      id: p.id,
      worldPoint: p.worldPoint.clone(),
      direction: p.direction.clone(),
      screenPosition: p.screenPosition.clone(),
      positiveLength: p.positiveLength,
      negativeLength: p.negativeLength,
      connections: p.connections,
    }));

    // Update nextPromptId to be higher than any existing prompt
    for (const prompt of this.linePrompts) {
      const match = prompt.id.match(/prompt_(\d+)/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num >= this.nextPromptId) {
          this.nextPromptId = num + 1;
        }
      }
    }

    // Update screen positions and shape preview
    this.updatePromptScreenPositions();
    if (this.linePrompts.length >= 3) {
      this.updateShapePreview();
    }
    this.visualizer.updatePromptVisuals(this.linePrompts, this.hoveredPromptId);
  }

  /**
   * Cleanup
   */
  dispose(): void {
    // Cancel any ongoing detection
    if (this.detectionAbortController) {
      this.detectionAbortController.abort();
      this.detectionAbortController = null;
    }

    this.lineDetectionService.dispose();
    this.lineRenderer.dispose();
    this.areaSelector.dispose();
    this.visualizer.dispose();
    this.sparkleParticles.dispose();
    this.shapeRendererModule.clear();
  }
}
